/**
 * Simple concurrency-limited queue for uploads.
 *
 * The hosting layer rejects too many simultaneous requests per client with a
 * 429 (without CORS headers, so the browser reports it as a network error).
 * Every upload goes through this queue so only a limited number run at once.
 */
class UploadQueue {

    constructor(concurrency = 2) {
        this.concurrency = Math.max(1, parseInt(concurrency, 10) || 1);
        this.running = 0;
        this.waiting = [];
    }

    /**
     * Run `task` (a function returning a promise) as soon as a slot is free.
     * Resolves/rejects with the outcome of the task.
     */
    run(task) {
        return new Promise((resolve, reject) => {
            this.waiting.push({ task, resolve, reject });
            this._next();
        });
    }

    _next() {
        if (this.running >= this.concurrency || this.waiting.length === 0) return;

        const { task, resolve, reject } = this.waiting.shift();
        this.running++;

        let promise;
        try {
            promise = Promise.resolve(task());
        } catch (e) {
            promise = Promise.reject(e);
        }

        promise
            .then(resolve, reject)
            .finally(() => {
                this.running--;
                this._next();
            });
    }
}

export default UploadQueue;
